import { useToasts } from '@brightdrop/bd-ui';
import { Box } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  SIDEKICK_FEATURE_EVENT,
  useSidekickFeatureEvent,
} from '../../featureEvents';
import { useConversation } from '../../hooks/useConversation';
import { useConversationBranches } from '../../hooks/useConversationBranches';
import { useDiscardConversation } from '../../hooks/useDiscardConversation';
import { useSavedMessageLookup } from '../../hooks/useSavedMessageLookup';
import { MOCK_SIDEKICK_SUGGESTION_CHIP_SET } from '../../mocks/chipDataset';
import {
  selectConversationMetadata,
  selectCurrentToolActivity,
  selectIsReconnecting,
  selectPendingHostUserMessage,
  selectRetryAttempt,
} from '../../state/aiSidekickChatSlice';
import { selectIsResetConfirmOpen } from '../../state/aiSidekickSlice';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import InputBar from '../InputBar';
import MessageList from '../MessageList';
import PanelZeroStateSuggestions from '../PanelZeroStateSuggestions';
import ResetConversationPrompt from '../ResetConversationPrompt';
import SidekickDisclaimerBanner from '../SidekickDisclaimerBanner';
import useStyles from './ChatInterface.styles';
import type { ChatInterfaceProps } from './types';
import { useComposerDraftGuard } from './use-composer-draft-guard';
import { usePendingHostUserMessage } from './usePendingHostUserMessage';

export type { ChatInterfaceProps } from './types';

const THINKING_MAX_ATTEMPTS = 3;
const NETWORK_TOAST_KEY = 'chat.error.network.toast';

/** Panel chat shell for messages, branch editing, retries, and the composer. */
function ChatInterface({
  isM1Enabled = false,
  isInputFocusEnabled = true,
  chipSet = MOCK_SIDEKICK_SUGGESTION_CHIP_SET,
  chipSetIsLoading = false,
}: ChatInterfaceProps) {
  const { classes } = useStyles();
  const { showErrorToast } = useToasts();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const emitFeatureEvent = useSidekickFeatureEvent();
  const handleNetworkError = useCallback(() => {
    showErrorToast(t(NETWORK_TOAST_KEY));
  }, [showErrorToast, t]);
  const {
    displayMessages,
    sendMessage,
    retry,
    stopResponse,
    isStreaming,
    isStopPending,
  } = useConversation({ onNetworkError: handleNetworkError });
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const discardConversation = useDiscardConversation();
  const { findArtifact } = useSavedMessageLookup();
  const { branchNavigation, isCreatingBranch, startBranch, switchBranch } =
    useConversationBranches({
      sendMessage,
      discardConversation,
      onEditedTurnRejected: handleNetworkError,
    });
  const isResetConfirmOpen = useSelector(selectIsResetConfirmOpen);
  const retryAttempt = useSelector(selectRetryAttempt);
  const currentToolActivity = useSelector(selectCurrentToolActivity);
  const isReconnecting = useSelector(selectIsReconnecting);
  const pendingHostUserMessage = useSelector(selectPendingHostUserMessage);
  const conversationMetadata = useSelector(selectConversationMetadata);
  const { composerKey, hasComposerDraft, handleComposerValueChange } =
    useComposerDraftGuard();
  const isConversationBusy = isCreatingBranch || isStreaming || isStopPending;
  const isEditInteractionBusy =
    isConversationBusy ||
    pendingHostUserMessage !== null ||
    conversationMetadata === null ||
    hasComposerDraft;

  const handleSubmitQuestion = useCallback(
    (text: string) => sendMessage(text),
    [sendMessage]
  );

  usePendingHostUserMessage({ isStreaming: isConversationBusy, sendMessage });

  const handleSubmitEdit = useCallback(
    async (messageId: string, content: string): Promise<boolean> => {
      try {
        const didStartBranch = await startBranch({ messageId, content });
        if (didStartBranch) {
          setEditingMessageId(null);
          emitFeatureEvent({
            type: SIDEKICK_FEATURE_EVENT.QUERY_EDITED,
            inputLength: content.trim().length,
          });
        }
        return didStartBranch;
      } catch (error) {
        handleNetworkError();
        if (import.meta.env.DEV) {
          console.warn('[ChatInterface] failed to create edited branch', error);
        }
        return false;
      }
    },
    [emitFeatureEvent, handleNetworkError, startBranch]
  );

  const handleSwitchBranch = useCallback(
    (conversationId: string) => {
      if (!switchBranch(conversationId)) {
        return;
      }
      setEditingMessageId(null);
      if (branchNavigation) {
        emitFeatureEvent({
          type: SIDEKICK_FEATURE_EVENT.BRANCH_SWITCHED,
          direction:
            conversationId === branchNavigation.previousConversationId
              ? 'previous'
              : 'next',
          branchCount: branchNavigation.total,
        });
      }
    },
    [branchNavigation, emitFeatureEvent, switchBranch]
  );
  const handleCancelEdit = useCallback(() => {
    setEditingMessageId(null);
  }, []);
  const editControls = useMemo(
    () =>
      isM1Enabled
        ? {
            editingMessageId,
            isBusy: isEditInteractionBusy,
            isCreatingBranch,
            branchNavigation,
            onEdit: setEditingMessageId,
            onCancelEdit: handleCancelEdit,
            onSubmitEdit: handleSubmitEdit,
            onSwitchBranch: handleSwitchBranch,
          }
        : undefined,
    [
      branchNavigation,
      editingMessageId,
      handleCancelEdit,
      handleSubmitEdit,
      handleSwitchBranch,
      isCreatingBranch,
      isEditInteractionBusy,
      isM1Enabled,
    ]
  );

  const hasMessages = displayMessages.length > 0 || isStreaming;
  const trailingSlot = isResetConfirmOpen ? <ResetConversationPrompt /> : null;
  // `retryAttempt` is `0 | 1 | 2 | 3`; narrow off the idle (`0`)
  // value so the prop matches `MessageList.thinkingAttempt`'s
  // `1 | 2 | 3 | undefined` shape without a type assertion.
  const thinkingAttempt: 1 | 2 | 3 | undefined =
    retryAttempt === 0 ? undefined : retryAttempt;

  return (
    <Box
      className={classes.root}
      data-testid="ai-sidekick-chat-interface"
      data-state={hasMessages ? 'threaded' : 'empty'}
    >
      <Box className={classes.messagesRegion}>
        {hasMessages ? (
          <MessageList
            isM1Enabled={isM1Enabled}
            messages={displayMessages}
            isStreaming={isStreaming}
            thinkingAttempt={thinkingAttempt}
            thinkingMaxAttempts={THINKING_MAX_ATTEMPTS}
            currentToolActivity={currentToolActivity}
            isReconnecting={isReconnecting}
            onRetry={retry}
            editControls={editControls}
            findArtifact={findArtifact}
            trailingSlot={trailingSlot}
          />
        ) : isM1Enabled ? null : (
          <PanelZeroStateSuggestions
            chipSet={chipSet}
            onSubmitQuestion={handleSubmitQuestion}
            isLoading={chipSetIsLoading}
          />
        )}
      </Box>
      {isM1Enabled ? (
        <Box className={classes.m1Footer} data-testid="ai-sidekick-m1-footer">
          {hasMessages ? null : (
            <PanelZeroStateSuggestions
              chipSet={chipSet}
              onSubmitQuestion={handleSubmitQuestion}
              isLoading={chipSetIsLoading}
              showEmptyStateCopy={false}
              isM1Enabled
            />
          )}
          <Box
            className={classes.m1SearchRegion}
            data-testid="ai-sidekick-m1-search-region"
          >
            <InputBar
              key={composerKey}
              isM1Enabled
              isInputFocusEnabled={isInputFocusEnabled}
              onSubmit={sendMessage}
              isStreaming={isConversationBusy}
              onStop={isStreaming ? stopResponse : undefined}
              onValueChange={handleComposerValueChange}
            />
            <SidekickDisclaimerBanner isM1Enabled />
          </Box>
        </Box>
      ) : (
        <>
          <SidekickDisclaimerBanner />
          <Box className={classes.inputRegion}>
            <InputBar
              key={composerKey}
              isInputFocusEnabled={isInputFocusEnabled}
              onSubmit={sendMessage}
              isStreaming={isConversationBusy}
              onValueChange={handleComposerValueChange}
            />
          </Box>
        </>
      )}
    </Box>
  );
}

export default ChatInterface;
